import { useState } from 'react';
import { compact, intersection, toString } from 'lodash';
import {
  AssetsGroups,
  DeleteAssetProps,
  DeleteBookmarkProps,
  DeleteBroadcastProps,
  DeleteDashboardProps,
  DeleteDynamicFilterProps,
  DeleteFieldProps,
  DeleteFolderProps,
  DeleteMetamodelProps,
  DeleteMultipleAssetsProps,
  DeletePresentationProps,
  DeleteReferenceTypeProps,
  DeleteReportProps,
  DeleteScenarioProps,
  DeleteSurveyProps,
  DeleteTraversalProps,
  DeleteType,
  DeleteViewpointProps,
  DeleteWorkspaceProps,
  ScenarioBasicDetails,
} from './types';
import { scenarioInterface } from 'modelInterface/scenarios/scenarioInterface';
import { useEffectOnce, useOnMount } from '@ardoq/hooks';
import { DialogBaseProps, ModalListOption } from '@ardoq/modal';
import { IconName } from '@ardoq/icons';
import { presentationOperations } from 'streams/presentations/presentationOperations';
import { type PresentationsStreamShape } from 'streams/presentations/types';
import {
  APIHomePageDashboard,
  APIPresentationAssetAttributes,
  APISlideAttributes,
  ArdoqId,
  AssetType,
  ComponentTypeCard,
  isVisualizationSlide,
} from '@ardoq/api-types';
import { flow } from 'lodash/fp';
import { isArdoqError } from '@ardoq/common-helpers';
import { pageApi, reportApi, workspaceApi } from '@ardoq/api';
import { logError } from '@ardoq/logging';
import { Features, featureOperations } from '@ardoq/features';
import { getFeatureContext } from 'streams/featureContext$';
import { AffectedEntitiesTable } from './AffectedEntityTable';
import { type I18nLocale, t } from '@ardoq/i18n';
import { type Locale } from '@ardoq/locale';

type ModalText = Partial<
  Pick<
    DialogBaseProps<any>,
    | 'title'
    | 'subtitle'
    | 'listOptions'
    | 'warningBlockMessage'
    | 'confirmButtonTitle'
    | 'text'
    | 'children'
  >
>;

const getItemsListed = (assetProps: AssetsGroups, i18nLocale: I18nLocale) => {
  return [
    ...(assetProps.folders || []).map(folder => ({
      iconName: IconName.FOLDER,
      strongText: folder.name,
      text: t(
        i18nLocale,
        'assetsBrowser:modals.delete.itemType.folder',
        'folder'
      ),
    })),

    ...(assetProps.workspaces || []).map(workspace => ({
      iconName: IconName.WORKSPACE,
      strongText: workspace.name,
      text: t(
        i18nLocale,
        'assetsBrowser:modals.delete.itemType.workspace',
        'workspace'
      ),
    })),

    ...(assetProps.presentations || []).map(presentation => ({
      iconName: IconName.PRESENTATION,
      strongText: presentation.name,
      text: t(
        i18nLocale,
        'assetsBrowser:modals.delete.itemType.presentation',
        'presentation'
      ),
    })),

    ...(assetProps.surveys || []).map(survey => ({
      iconName: IconName.SURVEYS,
      strongText: survey.name,
      text: t(
        i18nLocale,
        'assetsBrowser:modals.delete.itemType.survey',
        'survey'
      ),
    })),

    ...(assetProps.metamodels || []).map(metamodel => ({
      iconName: IconName.METAMODEL,
      strongText: metamodel.name,
      text: t(
        i18nLocale,
        'assetsBrowser:modals.delete.itemType.metamodel',
        'metamodel'
      ),
    })),

    ...(assetProps.scenarios || []).map(scenario => ({
      iconName: IconName.SCENARIO,
      strongText: scenario.name,
      text: t(
        i18nLocale,
        'assetsBrowser:modals.delete.itemType.scenario',
        'scenario'
      ),
    })),

    ...(assetProps.reports || []).map(report => ({
      iconName: IconName.DESCRIPTION,
      strongText: report.name,
      text: t(
        i18nLocale,
        'assetsBrowser:modals.delete.itemType.report',
        'report'
      ),
    })),

    ...(assetProps.dashboards || []).map(dashboard => ({
      iconName: IconName.DASHBOARD,
      strongText: dashboard.name,
      text: t(
        i18nLocale,
        'assetsBrowser:modals.delete.itemType.dashboard',
        'dashboard'
      ),
    })),

    ...(assetProps.viewpoints || []).map(viewpoint => ({
      iconName: IconName.VIEWPOINT,
      strongText: viewpoint.name,
      text: t(
        i18nLocale,
        'assetsBrowser:modals.delete.itemType.discoverViewpoint',
        'Discover viewpoint'
      ),
    })),

    ...(assetProps.traversals || []).map(traversal => ({
      iconName: IconName.VIEWPOINT,
      strongText: traversal.name,
      text: t(
        i18nLocale,
        'assetsBrowser:modals.delete.itemType.viewpoint',
        'viewpoint'
      ),
    })),

    ...(assetProps.broadcasts || []).map(broadcast => ({
      iconName: IconName.BROADCAST,
      strongText: broadcast.name,
      text: t(
        i18nLocale,
        'assetsBrowser:modals.delete.itemType.broadcast',
        'broadcast'
      ),
    })),

    ...(assetProps.bookmarks || []).map(bookmark => ({
      iconName: IconName.BOOKMARK,
      strongText: bookmark.name,
      text: t(
        i18nLocale,
        'assetsBrowser:modals.delete.itemType.bookmark',
        'bookmark'
      ),
    })),
  ] as ModalListOption[];
};

const getWorkspaceWarningBlockMessage = (
  numAffectedSurveys: number,
  i18nLocale: I18nLocale
) => {
  const baseWarning = t(
    i18nLocale,
    'assetsBrowser:modals.delete.workspaceWarning.base',
    "Once deleted, the workspace can't be recovered."
  );
  if (numAffectedSurveys === 0) return <>{baseWarning}</>;
  const affected =
    numAffectedSurveys === 1
      ? t(
          i18nLocale,
          'assetsBrowser:modals.delete.workspaceWarning.affectedSingular',
          '1 survey is based on this workspace. If the workspace is deleted, the survey may no longer work.'
        )
      : t(
          i18nLocale,
          'assetsBrowser:modals.delete.workspaceWarning.affectedPlural',
          { count: numAffectedSurveys },
          '{{count}} surveys are based on this workspace. If the workspace is deleted, the surveys may no longer work.'
        );
  return (
    <>
      <p>{affected}</p>
      {baseWarning}
    </>
  );
};

const getReferenceTypeWarningBlockMessage = (
  numAffectedSurveys: number,
  i18nLocale: I18nLocale
) => {
  const baseWarning = t(
    i18nLocale,
    'assetsBrowser:modals.delete.referenceTypeWarning.base',
    "Once deleted, the reference type can't be recovered."
  );
  if (numAffectedSurveys === 0) return <>{baseWarning}</>;
  const affected =
    numAffectedSurveys === 1
      ? t(
          i18nLocale,
          'assetsBrowser:modals.delete.referenceTypeWarning.affectedSingular',
          '1 survey uses this reference type. If the reference type is deleted, the survey may no longer work.'
        )
      : t(
          i18nLocale,
          'assetsBrowser:modals.delete.referenceTypeWarning.affectedPlural',
          { count: numAffectedSurveys },
          '{{count}} surveys use this reference type. If the reference type is deleted, the surveys may no longer work.'
        );
  return (
    <>
      <p>{affected}</p>
      {baseWarning}
    </>
  );
};

const getFolderModalContentProps = (
  assetProps: DeleteFolderProps,
  i18nLocale: I18nLocale
) => {
  const { name, assets } = assetProps;
  return {
    title: t(
      i18nLocale,
      'assetsBrowser:modals.delete.folder.title',
      'Delete folder'
    ),
    subtitle: t(
      i18nLocale,
      'assetsBrowser:modals.delete.folder.subtitle',
      { name },
      'Are you sure you want to delete "{{name}}" folder and all its content?'
    ),
    listOptions: getItemsListed(assets, i18nLocale),
  };
};

const getWorkspaceModalContentProps = (
  assetProps: DeleteWorkspaceProps,
  locale: Locale,
  i18nLocale: I18nLocale
) => {
  return {
    title: t(
      i18nLocale,
      'assetsBrowser:modals.delete.workspace.title',
      'Delete workspace'
    ),
    subtitle: t(
      i18nLocale,
      'assetsBrowser:modals.delete.workspace.subtitle',
      { name: assetProps.name },
      'Are you sure you want to delete "{{name}}" workspace?'
    ),
    confirmButtonTitle: t(
      i18nLocale,
      'assetsBrowser:modals.delete.workspace.confirmButton',
      'Delete workspace'
    ),
    warningBlockMessage: getWorkspaceWarningBlockMessage(
      assetProps.affectedSurveys.length,
      i18nLocale
    ),
    children: assetProps.affectedSurveys.length ? (
      <AffectedEntitiesTable
        affectedEntities={assetProps.affectedSurveys}
        affectedEntityType={AssetType.SURVEY}
        locale={locale}
        i18nLocale={i18nLocale}
      />
    ) : undefined,
  };
};

const getTraversalModalContentProps = (
  assetProps: DeleteTraversalProps,
  i18nLocale: I18nLocale
) => {
  return {
    title: t(
      i18nLocale,
      'assetsBrowser:modals.delete.traversal.title',
      'Delete viewpoint'
    ),
    subtitle: t(
      i18nLocale,
      'assetsBrowser:modals.delete.traversal.subtitle',
      { name: assetProps.name },
      'Are you sure you want to delete the "{{name}}" viewpoint?'
    ),
    warningBlockMessage: t(
      i18nLocale,
      'assetsBrowser:modals.delete.traversal.warning',
      "This viewpoint can't be recovered once it is deleted. This will not affect the component and reference types used in the viewpoint. Do you want to continue with this?"
    ),
  };
};

const getBookmarkModalContentProps = (
  assetProps: DeleteBookmarkProps,
  i18nLocale: I18nLocale
) => {
  return {
    title: t(
      i18nLocale,
      'assetsBrowser:modals.delete.bookmark.title',
      'Delete bookmark?'
    ),
    subtitle: t(
      i18nLocale,
      'assetsBrowser:modals.delete.bookmark.subtitle',
      { name: assetProps.name },
      'This bookmark "{{name}}" will be deleted permanently and cannot be recovered. Do you want to continue with this?'
    ),
  };
};

const getPresentationsContainingScenario = (
  scenarioId: ArdoqId,
  presentationsState: PresentationsStreamShape,
  slides: APISlideAttributes[]
): APIPresentationAssetAttributes[] =>
  compact(
    slides
      .filter(
        slide => isVisualizationSlide(slide) && slide.scenarioId === scenarioId
      )
      .map(slide =>
        presentationOperations.getPresentationThatContainsSlide(
          presentationsState,
          slide._id
        )
      )
  );

const getScenarioModalContentProps = (
  assetProps: DeleteScenarioProps,
  i18nLocale: I18nLocale,
  presentationsContainingScenario: APIPresentationAssetAttributes[]
) => {
  if (presentationsContainingScenario.length) {
    return {
      title: t(
        i18nLocale,
        'assetsBrowser:modals.delete.scenario.title',
        'Delete scenario'
      ),
      subtitle: t(
        i18nLocale,
        'assetsBrowser:modals.delete.scenario.inPresentations',
        'The scenario will be deleted from the following presentation(s):'
      ),
      listOptions: presentationsContainingScenario.map(presentation => ({
        text: presentation.name,
        iconName: IconName.PRESENTATION,
      })),
      warningBlockMessage: t(
        i18nLocale,
        'assetsBrowser:modals.delete.scenario.cannotBeUndone',
        'This cannot be undone'
      ),
    };
  }
  return {
    title: t(
      i18nLocale,
      'assetsBrowser:modals.delete.scenario.title',
      'Delete scenario'
    ),
    subtitle: t(
      i18nLocale,
      'assetsBrowser:modals.delete.scenario.subtitle',
      { name: assetProps.name },
      'Are you sure you want to delete "{{name}}" scenario?'
    ),
  };
};

const getPresentationModalContentProps = (
  assetProps: DeletePresentationProps,
  i18nLocale: I18nLocale
) => {
  return {
    title: t(
      i18nLocale,
      'assetsBrowser:modals.delete.presentation.title',
      'Delete presentation'
    ),
    subtitle: t(
      i18nLocale,
      'assetsBrowser:modals.delete.presentation.subtitle',
      { name: assetProps.name },
      'Are you sure you want to delete "{{name}}" presentation?'
    ),
  };
};

const getSurveyModalContentProps = (
  assetProps: DeleteSurveyProps,
  i18nLocale: I18nLocale
) => {
  return {
    title: t(
      i18nLocale,
      'assetsBrowser:modals.delete.survey.title',
      'Delete survey'
    ),
    subtitle: t(
      i18nLocale,
      'assetsBrowser:modals.delete.survey.subtitle',
      { name: assetProps.name },
      'Are you sure you want to delete "{{name}}" survey?'
    ),
  };
};

const getViewpointModalContentProps = (
  assetProps: DeleteViewpointProps,
  i18nLocale: I18nLocale
) => {
  const baseRecoverable = t(
    i18nLocale,
    'assetsBrowser:modals.delete.viewpoint.base',
    "The viewpoint can't be recovered."
  );
  const warningMessage =
    assetProps.affectedBroadcasts > 0 ? (
      <>
        {assetProps.affectedBroadcasts === 1
          ? t(
              i18nLocale,
              'assetsBrowser:modals.delete.viewpoint.affectedSingular',
              '1 running broadcast uses this viewpoint to share surveys. If the viewpoint is deleted, users will still be able to access the surveys from the Survey overview page.'
            )
          : t(
              i18nLocale,
              'assetsBrowser:modals.delete.viewpoint.affectedPlural',
              { count: assetProps.affectedBroadcasts },
              '{{count}} running broadcasts use this viewpoint to share surveys. If the viewpoint is deleted, users will still be able to access the surveys from the Survey overview page.'
            )}
        <br />
        <br />
      </>
    ) : undefined;
  return {
    title: t(
      i18nLocale,
      'assetsBrowser:modals.delete.viewpoint.title',
      'Delete viewpoint'
    ),
    subtitle: t(
      i18nLocale,
      'assetsBrowser:modals.delete.viewpoint.subtitle',
      { name: assetProps.name },
      'Are you sure you want to delete the "{{name}}" viewpoint?'
    ),
    warningBlockMessage: (
      <>
        {warningMessage}
        {baseRecoverable}
      </>
    ),
  };
};

const getBroadcastModalContentProps = (
  assetProps: DeleteBroadcastProps,
  i18nLocale: I18nLocale
) => {
  return {
    title: t(
      i18nLocale,
      'assetsBrowser:modals.delete.broadcast.title',
      'Delete broadcast'
    ),
    subtitle: t(
      i18nLocale,
      'assetsBrowser:modals.delete.broadcast.subtitle',
      { name: assetProps.name },
      'Are you sure you want to delete the "{{name}}" broadcast?'
    ),
    warningBlockMessage: t(
      i18nLocale,
      'assetsBrowser:modals.delete.broadcast.cannotBeUndone',
      'This cannot be undone'
    ),
  };
};

const getDashboardModalContentProps = (
  assetProps: DeleteDashboardProps,
  i18nLocale: I18nLocale
) => {
  return {
    title: t(
      i18nLocale,
      'assetsBrowser:modals.delete.dashboard.title',
      'Delete dashboard'
    ),
    subtitle: t(
      i18nLocale,
      'assetsBrowser:modals.delete.dashboard.subtitle',
      { name: assetProps.name },
      'Are you sure you want to delete the "{{name}}" dashboard?'
    ),
    warningBlockMessage: t(
      i18nLocale,
      'assetsBrowser:modals.delete.dashboard.cannotBeUndone',
      'This cannot be undone'
    ),
  };
};

const getMetamodelModalContentProps = (
  assetProps: DeleteMetamodelProps,
  i18nLocale: I18nLocale
) => {
  return {
    title: t(
      i18nLocale,
      'assetsBrowser:modals.delete.metamodel.title',
      'Delete metamodel'
    ),
    subtitle: t(
      i18nLocale,
      'assetsBrowser:modals.delete.metamodel.subtitle',
      { name: assetProps.name },
      'Are you sure you want to delete "{{name}}" metamodel?'
    ),
  };
};

const getReportModalContentProps = (
  assetProps: DeleteReportProps,
  i18nLocale: I18nLocale
) => {
  return {
    title: t(
      i18nLocale,
      'assetsBrowser:modals.delete.report.title',
      'Delete report'
    ),
    subtitle: t(
      i18nLocale,
      'assetsBrowser:modals.delete.report.subtitle',
      { name: assetProps.name },
      'Are you sure you want to delete "{{name}}" report?'
    ),
  };
};

const getQueryModalContentProps = (
  { name, affectedPerspectives }: DeleteDynamicFilterProps,
  i18nLocale: I18nLocale
) => ({
  title: t(
    i18nLocale,
    'assetsBrowser:modals.delete.query.title',
    'Delete graph filter?'
  ),
  subtitle: t(
    i18nLocale,
    'assetsBrowser:modals.delete.query.subtitle',
    { name },
    'The graph filter "{{name}}" will be deleted permanently.'
  ),
  text: affectedPerspectives?.length
    ? affectedPerspectives.length === 1
      ? t(
          i18nLocale,
          'assetsBrowser:modals.delete.query.affectedSingular',
          '1 saved perspective uses this graph filter:'
        )
      : t(
          i18nLocale,
          'assetsBrowser:modals.delete.query.affectedPlural',
          { count: affectedPerspectives.length },
          '{{count}} saved perspectives use this graph filter:'
        )
    : undefined,
  listOptions: affectedPerspectives.map(({ name }) => ({
    strongText: name,
  })),
  warningBlockMessage: affectedPerspectives?.length
    ? t(
        i18nLocale,
        'assetsBrowser:modals.delete.query.warning',
        'These perspectives will display incorrect results if the graph filter is deleted.'
      )
    : undefined,
});

const getFieldModalContentProps = (
  { name, affectedEntities }: DeleteFieldProps,
  i18nLocale: I18nLocale
) => {
  return {
    title: t(
      i18nLocale,
      'assetsBrowser:modals.delete.field.title',
      'Delete field from the organization'
    ),
    subtitle: t(
      i18nLocale,
      'assetsBrowser:modals.delete.field.subtitle',
      { name },
      'Are you sure you want to delete the field "{{name}}" from the organization?'
    ),
    text: t(
      i18nLocale,
      'assetsBrowser:modals.delete.field.text',
      'This will affect:'
    ),
    listOptions: [
      {
        iconName: IconName.WORKSPACE,
        strongText: `${affectedEntities.workspaceCount}`,
        text: t(
          i18nLocale,
          'assetsBrowser:modals.delete.field.affected.workspaces',
          'workspaces'
        ),
      },
      {
        iconName: IconName.SURVEYS,
        strongText: affectedEntities.surveyCount,
        text: t(
          i18nLocale,
          'assetsBrowser:modals.delete.field.affected.surveys',
          'surveys'
        ),
      },
      {
        iconName: IconName.DESCRIPTION,
        strongText: affectedEntities.reportCount,
        text: t(
          i18nLocale,
          'assetsBrowser:modals.delete.field.affected.report',
          'report'
        ),
      },
      {
        iconName: IconName.COMPONENT,
        strongText: affectedEntities.componentCount,
        text: t(
          i18nLocale,
          'assetsBrowser:modals.delete.field.affected.components',
          'components'
        ),
      },
      {
        iconName: IconName.REFERENCE,
        strongText: affectedEntities.referenceCount,
        text: t(
          i18nLocale,
          'assetsBrowser:modals.delete.field.affected.references',
          'references'
        ),
      },
    ] as ModalListOption[],
  };
};

const getReferenceTypeModalContentProps = (
  assetProps: DeleteReferenceTypeProps,
  locale: Locale,
  i18nLocale: I18nLocale
) => {
  const hasAffectedAssets = assetProps.affectedSurveys.length > 0;
  return {
    title: t(
      i18nLocale,
      'assetsBrowser:modals.delete.referenceType.title',
      { name: assetProps.name },
      'Delete "{{name}}" reference type permanently?'
    ),
    confirmButtonTitle: t(
      i18nLocale,
      'assetsBrowser:modals.delete.referenceType.confirmButton',
      'Delete reference type'
    ),
    warningBlockMessage: getReferenceTypeWarningBlockMessage(
      assetProps.affectedSurveys.length,
      i18nLocale
    ),
    text: hasAffectedAssets ? (
      <AffectedEntitiesTable
        affectedEntities={assetProps.affectedSurveys}
        affectedEntityType={AssetType.SURVEY}
        locale={locale}
        i18nLocale={i18nLocale}
      />
    ) : undefined,
  };
};

const getMultipleAssetsModalContentProps = (
  assetProps: DeleteMultipleAssetsProps,
  i18nLocale: I18nLocale
) => {
  const assetsCount: number = Object.values(assetProps.assets).reduce(
    (acc, assets) => acc + assets.length,
    0
  );
  const isSingle = assetsCount === 1;
  return {
    title: isSingle
      ? t(
          i18nLocale,
          'assetsBrowser:modals.delete.multiple.titleSingular',
          'Delete 1 asset'
        )
      : t(
          i18nLocale,
          'assetsBrowser:modals.delete.multiple.titlePlural',
          { count: assetsCount },
          'Delete {{count}} assets'
        ),
    subtitle: isSingle
      ? t(
          i18nLocale,
          'assetsBrowser:modals.delete.multiple.subtitleSingular',
          'Are you sure you want to delete this 1 selected asset?'
        )
      : t(
          i18nLocale,
          'assetsBrowser:modals.delete.multiple.subtitlePlural',
          { count: assetsCount },
          'Are you sure you want to delete these {{count}} selected assets?'
        ),
    listOptions: getItemsListed(assetProps.assets, i18nLocale),
  };
};

type GetModalContentPropsArgs = {
  assetProps: DeleteAssetProps;
  locale: Locale;
  i18nLocale: I18nLocale;
  presentationsState: PresentationsStreamShape;
  slides: APISlideAttributes[];
};

export const getModalContentProps = ({
  assetProps,
  locale,
  i18nLocale,
  presentationsState,
  slides,
}: GetModalContentPropsArgs): ModalText => {
  if (assetProps.deleteType === DeleteType.FOLDER)
    return getFolderModalContentProps(assetProps, i18nLocale);
  if (assetProps.deleteType === DeleteType.WORKSPACE)
    return getWorkspaceModalContentProps(assetProps, locale, i18nLocale);
  if (assetProps.deleteType === DeleteType.SCENARIO)
    return getScenarioModalContentProps(
      assetProps,
      i18nLocale,
      getPresentationsContainingScenario(
        assetProps.id,
        presentationsState,
        slides
      )
    );
  if (assetProps.deleteType === DeleteType.PRESENTATION)
    return getPresentationModalContentProps(assetProps, i18nLocale);
  if (assetProps.deleteType === DeleteType.MULTIPLE)
    return getMultipleAssetsModalContentProps(assetProps, i18nLocale);
  if (assetProps.deleteType === DeleteType.FIELD)
    return getFieldModalContentProps(assetProps, i18nLocale);
  if (assetProps.deleteType === DeleteType.SURVEY)
    return getSurveyModalContentProps(assetProps, i18nLocale);
  if (assetProps.deleteType === DeleteType.BROADCAST)
    return getBroadcastModalContentProps(assetProps, i18nLocale);
  if (assetProps.deleteType === DeleteType.DASHBOARD)
    return getDashboardModalContentProps(assetProps, i18nLocale);
  if (assetProps.deleteType === DeleteType.METAMODEL)
    return getMetamodelModalContentProps(assetProps, i18nLocale);
  if (assetProps.deleteType === DeleteType.REPORT)
    return getReportModalContentProps(assetProps, i18nLocale);
  if (assetProps.deleteType === DeleteType.VIEWPOINT)
    return getViewpointModalContentProps(assetProps, i18nLocale);
  if (assetProps.deleteType === DeleteType.TRAVERSAL)
    return getTraversalModalContentProps(assetProps, i18nLocale);
  if (assetProps.deleteType === DeleteType.REFERENCE_TYPE)
    return getReferenceTypeModalContentProps(assetProps, locale, i18nLocale);
  if (assetProps.deleteType === DeleteType.BOOKMARK)
    return getBookmarkModalContentProps(assetProps, i18nLocale);
  if (assetProps.deleteType === DeleteType.QUERY)
    return getQueryModalContentProps(assetProps, i18nLocale);

  // ensure we exhaust all possibilities of event-type
  assetProps satisfies never;
  return { title: '' };
};

const collectWorkspacesIds = (deleteAssetProps: DeleteAssetProps) => {
  if (deleteAssetProps.deleteType === DeleteType.MULTIPLE) {
    return deleteAssetProps.assets.workspaces.map(({ id }) => id);
  }
  if (deleteAssetProps.deleteType === DeleteType.WORKSPACE) {
    return [deleteAssetProps.id];
  }
  return [];
};

export const collectWorkspaceNamesById = (
  deleteAssetProps: DeleteAssetProps
): Record<ArdoqId, string> => {
  if (deleteAssetProps.deleteType === DeleteType.MULTIPLE) {
    return Object.fromEntries(
      deleteAssetProps.assets.workspaces.map(({ id, name }) => [id, name])
    );
  }
  if (deleteAssetProps.deleteType === DeleteType.WORKSPACE) {
    return deleteAssetProps.id
      ? { [deleteAssetProps.id]: deleteAssetProps.name }
      : {};
  }
  return {};
};

const collectScenarioIds = (deleteAssetProps: DeleteAssetProps) => {
  if (deleteAssetProps.deleteType === DeleteType.MULTIPLE) {
    return deleteAssetProps.assets.scenarios.map(({ id }) => id);
  }
  return [];
};

const collectReportIds = (deleteAssetProps: DeleteAssetProps) => {
  if (deleteAssetProps.deleteType === DeleteType.MULTIPLE) {
    return deleteAssetProps.assets.reports.map(({ id }) => id);
  }
  if (deleteAssetProps.deleteType === DeleteType.REPORT) {
    return [deleteAssetProps.id];
  }
  return [];
};

const deleteTypeToAssetKeyMap: Partial<Record<DeleteType, keyof AssetsGroups>> =
  {
    [DeleteType.REPORT]: 'reports',
    [DeleteType.DASHBOARD]: 'dashboards',
    [DeleteType.PRESENTATION]: 'presentations',
    [DeleteType.SURVEY]: 'surveys',
    [DeleteType.TRAVERSAL]: 'traversals',
  };

const deletedAssetHasId = (
  deleteAssetProps: DeleteAssetProps
): deleteAssetProps is DeleteAssetProps & { id: string } =>
  'id' in deleteAssetProps && Boolean(deleteAssetProps.id);

const collectEntityIds = (
  deleteAssetProps: DeleteAssetProps,
  entityType: DeleteType
): ArdoqId[] => {
  const assetKey = deleteTypeToAssetKeyMap[entityType];
  if (!assetKey) return [];
  if (deleteAssetProps.deleteType === DeleteType.MULTIPLE) {
    return deleteAssetProps.assets[assetKey].map(({ id }) => id);
  }
  if (
    deleteAssetProps.deleteType === entityType &&
    deletedAssetHasId(deleteAssetProps)
  ) {
    return [deleteAssetProps.id];
  }
  return [];
};

const getSafeScenarioNameById: (_id: ArdoqId) => string = flow([
  scenarioInterface.getScenarioNameById,
  toString,
]);

export const useCheckIfWsInUse = (deleteAssetProps: DeleteAssetProps) => {
  const wsIds = collectWorkspacesIds(deleteAssetProps);
  const workspaceNamesById = collectWorkspaceNamesById(deleteAssetProps);
  const scenarioIds = new Set(collectScenarioIds(deleteAssetProps));
  const hasWorkspaces = Boolean(wsIds.length);
  const [{ loading, workspaceNamesInUse, scenariosUsingSelectedWs }, setState] =
    useState<{
      loading: boolean;
      workspaceNamesInUse: string[];
      scenariosUsingSelectedWs: ScenarioBasicDetails[];
    }>({
      loading: hasWorkspaces,
      workspaceNamesInUse: [],
      scenariosUsingSelectedWs: [],
    });

  useOnMount(() => {
    (async () => {
      if (!hasWorkspaces) return;
      const validIds = wsIds.filter(id => id !== undefined) as ArdoqId[];
      const result = await workspaceApi.usage(validIds);
      if (isArdoqError(result)) {
        // TODO[ARD-22935]: Perhaps there should be some error handling here
        logError(result);
        return;
      }
      const processedResult =
        deleteAssetProps.deleteType === DeleteType.MULTIPLE
          ? result.filter(({ _id }) => !scenarioIds.has(_id))
          : result;
      const allWsIdsInUse = processedResult.flatMap(
        ({ workspaceIds }) => workspaceIds
      );

      setState({
        loading: false,
        workspaceNamesInUse: intersection(validIds, allWsIdsInUse).map(
          wsId => workspaceNamesById[wsId!] ?? ''
        ),
        scenariosUsingSelectedWs: processedResult.map<ScenarioBasicDetails>(
          ({ _id }) => ({
            name: getSafeScenarioNameById(_id),
            _id,
          })
        ),
      });
    })();
  });
  return {
    loading,
    workspaceNamesInUse,
    scenariosUsingSelectedWs,
    canShowDeleteDialog: !loading && workspaceNamesInUse.length === 0,
  };
};

export const useCheckIfReportInUse = (deleteAssetProps: DeleteAssetProps) => {
  const reportIds = collectReportIds(deleteAssetProps);
  const hasReports = Boolean(reportIds.length);
  const [
    {
      dashboardsUsingSelectedReports,
      totalDashboardsUsingSelectedReports,
      loading,
    },
    setState,
  ] = useState<{
    loading: boolean;
    totalDashboardsUsingSelectedReports: number;
    dashboardsUsingSelectedReports: Array<{ _id: ArdoqId; name: string }>;
  }>({
    loading: hasReports,
    totalDashboardsUsingSelectedReports: 0,
    dashboardsUsingSelectedReports: [],
  });
  useEffectOnce(() => {
    (async () => {
      if (!hasReports) return;
      const result = await reportApi.usage(reportIds);
      if (isArdoqError(result)) {
        logError(result);
        return;
      }
      setState({
        loading: false,
        totalDashboardsUsingSelectedReports: result.totalUseCount,
        dashboardsUsingSelectedReports: result.dashboards,
      });
    })();
  });
  return {
    loading,
    dashboardsUsingSelectedReports,
    totalDashboardsUsingSelectedReports,
  };
};

const findHomepageDashboardName = ({
  homePageDashboards,
  dashboardIds,
  deleteAssetProps,
}: {
  homePageDashboards: APIHomePageDashboard[];
  dashboardIds: ArdoqId[];
  deleteAssetProps: DeleteAssetProps;
}): string | null => {
  const homePageDashboardId = homePageDashboards.find(({ _id }) =>
    dashboardIds.includes(_id)
  )?._id;
  if (!homePageDashboardId) {
    return null;
  }
  if (deleteAssetProps.deleteType === DeleteType.DASHBOARD) {
    return deleteAssetProps.name;
  }
  if (deleteAssetProps.deleteType === DeleteType.MULTIPLE) {
    return (
      deleteAssetProps.assets.dashboards.find(d => d.id === homePageDashboardId)
        ?.name ?? null
    );
  }
  return null;
};

export const HOMEPAGE_LINKED_ASSETS: DeleteType[] = [
  DeleteType.REPORT,
  DeleteType.DASHBOARD,
  DeleteType.TRAVERSAL,
  DeleteType.PRESENTATION,
  DeleteType.SURVEY,
  DeleteType.MULTIPLE,
];

const collectUsedAssetsIds = (deleteAssetProps: DeleteAssetProps) => ({
  dashboardIds: collectEntityIds(deleteAssetProps, DeleteType.DASHBOARD),
  reportIds: collectEntityIds(deleteAssetProps, DeleteType.REPORT),
  presentationIds: collectEntityIds(deleteAssetProps, DeleteType.PRESENTATION),
  surveyIds: collectEntityIds(deleteAssetProps, DeleteType.SURVEY),
  traversalIds: collectEntityIds(deleteAssetProps, DeleteType.TRAVERSAL),
});

const findAssetNameInHomePageCards = ({
  cards,
  dashboardIds,
  reportIds,
  presentationIds,
  surveyIds,
  traversalIds,
  deleteAssetProps,
}: {
  cards: ComponentTypeCard[];
  dashboardIds: ArdoqId[];
  reportIds: ArdoqId[];
  presentationIds: ArdoqId[];
  surveyIds: ArdoqId[];
  traversalIds: ArdoqId[];
  deleteAssetProps: DeleteAssetProps;
}): string | null => {
  const cardAssetIds: Array<{
    id: ArdoqId;
    assetGroupKey: keyof AssetsGroups;
  }> = [
    ...dashboardIds.map(id => ({ id, assetGroupKey: 'dashboards' as const })),
    ...reportIds.map(id => ({ id, assetGroupKey: 'reports' as const })),
    ...presentationIds.map(id => ({
      id,
      assetGroupKey: 'presentations' as const,
    })),
    ...surveyIds.map(id => ({ id, assetGroupKey: 'surveys' as const })),
    ...traversalIds.map(id => ({
      id,
      assetGroupKey: 'traversals' as const,
    })),
  ];

  const firstMatchingCard = cards.find(
    card =>
      Boolean(card.assetLink?.entityId) &&
      cardAssetIds.some(({ id }) => id === card.assetLink?.entityId)
  );

  if (!firstMatchingCard) return null;

  const match = cardAssetIds.find(
    ({ id }) => id === firstMatchingCard.assetLink?.entityId
  )!;

  if (deleteAssetProps.deleteType === DeleteType.MULTIPLE) {
    return (
      deleteAssetProps.assets[match.assetGroupKey].find(a => a.id === match.id)
        ?.name ?? null
    );
  }
  if ('name' in deleteAssetProps) {
    return deleteAssetProps.name;
  }
  return null;
};

export const useCheckIfResourceInUseByHomePage = (
  deleteAssetProps: DeleteAssetProps
) => {
  const { dashboardIds, reportIds, presentationIds, surveyIds, traversalIds } =
    collectUsedAssetsIds(deleteAssetProps);
  const hasRelevantIds = Boolean(
    dashboardIds.length ||
    reportIds.length ||
    presentationIds.length ||
    surveyIds.length ||
    traversalIds.length
  );
  const homePageEnabled = featureOperations.hasFeature(
    getFeatureContext(),
    Features.FOUNDATION_HOME
  );

  const [{ assetUsedInHomePageName, loading }, setState] = useState<{
    loading: boolean;
    assetUsedInHomePageName: string | null;
  }>({
    loading: hasRelevantIds && homePageEnabled,
    assetUsedInHomePageName: null,
  });
  useEffectOnce(() => {
    (async () => {
      if (!hasRelevantIds || !homePageEnabled) return;
      const result = await pageApi.fetchHomePage();
      if (isArdoqError(result)) {
        logError(result);
        setState({ loading: false, assetUsedInHomePageName: null });
        return;
      }
      setState({
        loading: false,
        assetUsedInHomePageName:
          findHomepageDashboardName({
            homePageDashboards: result.dashboards,
            dashboardIds,
            deleteAssetProps,
          }) ??
          findAssetNameInHomePageCards({
            cards: result.cards,
            dashboardIds,
            reportIds,
            presentationIds,
            surveyIds,
            traversalIds,
            deleteAssetProps,
          }),
      });
    })();
  });
  return {
    loading,
    assetUsedInHomePageName,
  };
};
